import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface DataItem {
  label: string;
  fobPerUnit: number;
  volume: number;
}

interface Props {
  data: DataItem[];
  unitLabel: string;
  formatValue?: (value: number) => string;
}

const DistributionBarChart: React.FC<Props> = ({ data, unitLabel, formatValue }) => {
  const { t } = useTranslation('settlements');
  const { darkMode } = useUserStore();

  const chartData = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.fobPerUnit - a.fobPerUnit);
    return {
      labels: sorted.map((d) => d.label),
      fobPerUnit: sorted.map((d) => Number(d.fobPerUnit.toFixed(2))),
      volume: sorted.map((d) => Math.round(d.volume)),
    };
  }, [data]);

  const weightedAvg = useMemo(() => {
    const totalFob = data.reduce((sum, d) => sum + d.fobPerUnit * d.volume, 0);
    const totalVol = data.reduce((sum, d) => sum + d.volume, 0);
    return totalVol > 0 ? totalFob / totalVol : 0;
  }, [data]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '60%',
          dataLabels: { position: 'right' as const },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) =>
          formatValue ? formatValue(val) : `$${val.toFixed(2)}`,
        offsetX: 5,
        style: {
          fontSize: '11px',
          colors: [darkMode ? '#d4d4d8' : '#3f3f46'],
        },
      },
      xaxis: {
        categories: chartData.labels,
        labels: {
          formatter: (val: string) =>
            formatValue ? formatValue(Number(val)) : `$${Number(val).toFixed(2)}`,
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: '12px' },
          maxWidth: 140,
        },
      },
      annotations: {
        xaxis: [
          {
            x: weightedAvg,
            borderColor: darkMode ? '#a1a1aa' : '#71717a',
            strokeDashArray: 4,
            label: {
              text: `${t('Promedio')}: ${formatValue ? formatValue(weightedAvg) : `$${weightedAvg.toFixed(2)}`}`,
              position: 'top',
              orientation: 'horizontal',
              style: {
                fontSize: '11px',
                color: darkMode ? '#e4e4e7' : '#3f3f46',
                background: darkMode ? '#27272a' : '#f4f4f5',
                padding: { left: 6, right: 6, top: 2, bottom: 2 },
              },
            },
          },
        ],
      },
      colors: ['#6366f1'],
      grid: {
        borderColor: darkMode ? '#27272a' : '#e4e4e7',
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      tooltip: {
        y: {
          formatter: (val: number) =>
            formatValue ? formatValue(val) : `$${val.toFixed(2)}`,
          title: { formatter: () => `${unitLabel}: ` },
        },
      },
    }),
    [chartData.labels, darkMode, weightedAvg, formatValue, unitLabel, t]
  );

  const series = [{ name: unitLabel, data: chartData.fobPerUnit }];

  const chartHeight = Math.max(200, chartData.labels.length * 36 + 40);

  return (
    <ReactApexChart
      options={options}
      series={series}
      type='bar'
      height={chartHeight}
      width='100%'
    />
  );
};

export default DistributionBarChart;
